import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faMapMarkerAlt,
  faRoute,
  faQuestionCircle,
  faChartPie,
  faTasks,
  faHotel,
  faAd,
  faShare,
  faCloudSun,
  faLanguage,
  faMap,
  faHiking,
  faComments,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../components/Navbar';
import AdPageLayout from '../components/AdPageLayout';
import { PayPalButtons } from "@paypal/react-paypal-js";
import { AuthContext } from '../contexts/AuthContext';
import Toast from '../components/Toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { updateSubscriptionStatus } from '../services/api';
import SubscriptionManagement from '../components/SubscriptionManagement';
import PayPalSubscriptionProvider from '../components/PayPalSubscriptionProvider';
import { useLocation, useHistory } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7f9;
`;

const PageContainer = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
  background: #f5f7f9;
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const PlanHeader = styled.div`
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;
  margin-bottom: 20px;
`;

const PlanTitle = styled.h2`
  color: #316C6E;
  margin: 0 0 10px 0;
`;

const PlanPrice = styled.div`
  font-size: 2.5em;
  color: #316C6E;
  font-weight: bold;
  margin: 15px 0;

  span {
    font-size: 0.4em;
    color: #666;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  padding: 12px 0;
  color: ${props => props.available ? '#333' : '#999'};
  
  svg {
    width: 20px;
    margin-right: 15px;
    color: ${props => props.available ? '#316C6E' : '#ccc'};
  }

  .feature-icon {
    width: 20px;
    margin-right: 15px;
    color: #316C6E;
  }
`;

const SubscribeButton = styled.button`
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 15px;
  font-size: 1.1em;
  cursor: pointer;
  margin-top: 30px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #254F51;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const PaymentContainer = styled.div`
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const PaymentHeader = styled.h3`
  color: #316C6E;
  text-align: center;
  margin-bottom: 20px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const features = {
  free: [
    { text: 'curated', icon: faMapMarkerAlt },
    { text: 'faq', icon: faQuestionCircle },
    { text: 'budget', icon: faChartPie },
    { text: 'customActivities', icon: faTasks },
    { text: 'hotelBookings', icon: faHotel },
    { text: 'shareTrips', icon: faShare },
    { text: 'weather', icon: faCloudSun },
    { text: 'language', icon: faLanguage }
  ],
  premium: [
    { text: 'allFreeFeatures', icon: faCheck },
    { text: 'unlimitedTrips', icon: faRoute },
    { text: 'adFree', icon: faTimes },
    { text: 'advancedBudget', icon: faChartPie },
    { text: 'offlineMaps', icon: faMap },
    { text: 'noBookingFees', icon: faHotel },
    { text: 'liveChat', icon: faComments }
  ]
};

const SubscriptionPage = () => {
  const [showPayment, setShowPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const { user, refreshUser } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const { isAuthenticated } = useContext(AuthContext);
  const { language } = useLanguage();
  const t = translations[language].subscription;

  useEffect(() => {
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      history.replace('/login?redirectTo=/subscription');
    }
  }, [isAuthenticated, history]);

  const handleSubscribe = () => {
    setShowPayment(true);
  };

  const createSubscription = async (data, actions) => {
    try {
      console.log('Creating subscription with plan ID:', process.env.REACT_APP_PAYPAL_PLAN_ID);
      return actions.subscription.create({
        'plan_id': process.env.REACT_APP_PAYPAL_PLAN_ID,
        'application_context': {
          'shipping_preference': 'NO_SHIPPING',
          'user_action': 'SUBSCRIBE_NOW',
          'payment_method': {
            'payer_selected': 'PAYPAL',
            'payee_preferred': 'IMMEDIATE_PAYMENT_REQUIRED'
          }
        }
      });
    } catch (error) {
      console.error('Error creating subscription:', error);
      setToastMessage('Failed to create subscription. Please try again.');
      setToastType('error');
      return null;
    }
  };

  const onApprove = async (data, actions) => {
    try {
      setIsLoading(true);
      const response = await updateSubscriptionStatus({
        subscriptionId: data.subscriptionID,
        status: 'premium'
      });

      if (response) {
        await refreshUser();
        setToastMessage('Successfully upgraded to Premium!');
        setToastType('success');
        setShowPayment(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
      setToastMessage('Failed to process subscription');
      setToastType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const onError = (err) => {
    console.error('PayPal Error:', err);
    setToastMessage('Payment failed. Please try again.');
    setToastType('error');
  };

  const onCancel = (data) => {
    console.log('Subscription cancelled:', data);
    setToastMessage('Subscription cancelled.');
    setToastType('info');
  };

  const renderPremiumCard = () => {
    if (showPayment) {
      return (
        <PlanCard>
          <PlanHeader>
            <PlanTitle>{t.premium.title}</PlanTitle>
            <PlanPrice>{t.premium.price} <span>{t.premium.perMonth}</span></PlanPrice>
          </PlanHeader>
          <PaymentContainer id="payment-section">
            <PaymentHeader>{t.premium.completeSubscription}</PaymentHeader>
            <PayPalButtons
              createSubscription={createSubscription}
              onApprove={onApprove}
              onError={onError}
              onCancel={onCancel}
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'subscribe'
              }}
              forceReRender={[process.env.REACT_APP_PAYPAL_PLAN_ID]}
            />
          </PaymentContainer>
        </PlanCard>
      );
    }

    return (
      <PlanCard>
        <PlanHeader>
          <PlanTitle>{t.premium.title}</PlanTitle>
          <PlanPrice>{t.premium.price} <span>{t.premium.perMonth}</span></PlanPrice>
        </PlanHeader>
        <FeatureList>
          {features.premium.map((feature, index) => (
            <FeatureItem key={index} available={true}>
              <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
              {t.features[feature.text]}
            </FeatureItem>
          ))}
        </FeatureList>
        <SubscribeButton 
          onClick={handleSubscribe}
          disabled={user?.subscription_status === 'premium'}
        >
          {user?.subscription_status === 'premium' ? t.premium.currentPlan : t.premium.upgradeButton}
        </SubscribeButton>
      </PlanCard>
    );
  };

  return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          {user?.subscription_status === 'premium' ? (
            <>
              {user?.paypal_subscription_id ? (
                <SubscriptionManagement subscriptionId={user.paypal_subscription_id} />
              ) : (
                <div style={{ 
                  background: 'white', 
                  padding: '20px', 
                  borderRadius: '8px',
                  textAlign: 'center' 
                }}>
                  <h2 style={{ color: '#316C6E' }}>{t.status.title}</h2>
                  <p>{t.status.missingId}</p>
                  <p>{t.status.currentId} {user.paypal_subscription_id}</p>
                  <p>{t.status.tryLogout}</p>
                </div>
              )}
            </>
          ) : (
            <PayPalSubscriptionProvider>
              <PlansContainer>
                {renderPremiumCard()}
                <PlanCard>
                  <PlanHeader>
                    <PlanTitle>{t.free.title}</PlanTitle>
                    <PlanPrice>{t.free.price} <span>{t.free.perMonth}</span></PlanPrice>
                  </PlanHeader>
                  <FeatureList>
                    {features.free.map((feature, index) => (
                      <FeatureItem key={index} available={true}>
                        <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
                        {t.features[feature.text]}
                      </FeatureItem>
                    ))}
                  </FeatureList>
                  <SubscribeButton disabled>
                    {user?.subscription_status === 'free' ? t.premium.currentPlan : t.free.title}
                  </SubscribeButton>
                </PlanCard>
              </PlansContainer>
            </PayPalSubscriptionProvider>
          )}
        </PageContainer>
      </AdPageLayout>
      {isLoading && (
        <LoadingOverlay>
          <LoadingSpinner />
          <div>Processing your subscription...</div>
        </LoadingOverlay>
      )}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage('')}
        />
      )}
    </PageWrapper>
  );
};

export default SubscriptionPage; 